import React from 'react';
import { PRODUCTS } from '../config/columns.config';
import { formatMoney } from '../utils/formatters';
import { useTheme, alpha } from '@mui/material/styles';

const TerritorySummaryTable = ({ territories, config }) => {
    const theme = useTheme();
    const isSales = config?.reportCategory === 'sales';

    const calculateProRata = (achieved, budget) => {
        if (!budget || !config?.dateRange?.start || !config?.dateRange?.end) {
            return 0;
        }

        const startDate = new Date(config.dateRange.start);
        const endDate = new Date(config.dateRange.end);
        const today = new Date();

        const totalDuration = endDate - startDate;
        if (totalDuration <= 0) return 0;

        let timeElapsed = 0;
        if (today < startDate) {
            timeElapsed = 0;
        } else if (today > endDate) {
            timeElapsed = totalDuration;
        } else {
            timeElapsed = today - startDate;
        }

        const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24)) + 1;
        const daysPassed = Math.ceil(timeElapsed / (1000 * 60 * 60 * 24)) + 1;

        const proRataBudget = (budget / totalDays) * daysPassed;

        return proRataBudget > 0 ? (achieved / proRataBudget) * 100 : 0;
    };

    return (
        <table className="w-full text-sm border-collapse table-fixed">
            <colgroup>
                <col className="w-16" />
                <col className="w-48" />

                {PRODUCTS.map(p => <col key={p.key} className="w-24" />)}

                <col className="w-24" />
                {isSales && (
                    <>
                        <col className="w-24" />
                        <col className="w-24" />
                        <col className="w-24" />
                    </>
                )}
            </colgroup>
            <tbody>
                {territories.map(t => (
                    <React.Fragment key={t.territory}>
                        {t.rows.map((row, idx) => {
                            const percentageAchieved = row.budget ? (row.total / row.budget) * 100 : 0;
                            const proRataAchieved = calculateProRata(row.total, row.budget);
                            const displayBudget = row.budget === null ? '--' : formatMoney(row.budget);
                            const isTotalRow = row.label.toLowerCase().includes('total');
                            const rowClass = isTotalRow
                                ? "font-bold border-b transition-colors"
                                : "border-b transition-colors";

                            return (
                                <tr
                                    key={`${t.territory}-${row.label}`}
                                    className={rowClass}
                                    style={isTotalRow ? {
                                        backgroundColor: alpha(theme.palette.text.primary, 0.12),
                                        borderColor: theme.palette.divider
                                    } : {
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider
                                    }}
                                >
                                    {idx === 0 && (
                                        <td
                                            rowSpan={t.rows.length}
                                            className="p-2 font-bold text-center align-middle border-b"
                                            style={{
                                                color: theme.palette.warning.main,
                                                backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                                                borderColor: theme.palette.divider
                                            }}
                                        >
                                            {t.territory}
                                        </td>
                                    )}

                                    <td 
                                        className="p-4 font-medium truncate"
                                        style={{ color: isTotalRow ? theme.palette.text.primary : theme.palette.text.secondary }}
                                    >
                                        {row.label}
                                    </td>

                                    {PRODUCTS.map(p => (
                                        <td 
                                            key={p.key} 
                                            className="p-4 text-right truncate"
                                            style={{ color: isTotalRow ? theme.palette.warning.main : theme.palette.text.secondary }}
                                        >
                                            {formatMoney(row.values[p.key] || 0)}
                                        </td>
                                    ))}

                                    <td 
                                        className="p-4 text-right font-semibold truncate"
                                        style={{ color: isTotalRow ? theme.palette.warning.main : theme.palette.text.primary }}
                                    >
                                        {formatMoney(row.total)}
                                    </td>

                                    {isSales && (
                                        <>
                                            <td 
                                                className="p-4 text-right truncate"
                                                style={{ color: isTotalRow ? theme.palette.warning.main : theme.palette.text.secondary }}
                                            >
                                                {displayBudget}
                                            </td>
                                            <td 
                                                className="p-4 text-right truncate"
                                                style={{ color: isTotalRow ? theme.palette.warning.main : theme.palette.text.secondary }}
                                            >
                                                {row.budget === null ? '--' : `${percentageAchieved.toFixed(2)} %`}
                                            </td>
                                            <td 
                                                className="p-4 text-right truncate"
                                                style={{ color: isTotalRow ? theme.palette.warning.main : theme.palette.text.secondary }}
                                            >
                                                {row.budget === null ? '--' : `${proRataAchieved.toFixed(2)} %`}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    );
};

export default TerritorySummaryTable;